import React from 'react';
import { Box, Button, FormHelperText, TextField, Typography } from '@material-ui/core';
import { getIn, useFormikContext } from 'formik';
import i18n from 'i18n-js';
import ObjectID from 'bson-objectid';
import { ReservationBlockedDate } from 'store/restaurantDetailsStore/reducer';

export default function ReservationBlockedDatesSection() {
    const { values, errors, submitCount, isSubmitting, setValues } =
        useFormikContext<{ onlineRequestBlockedDates: ReservationBlockedDate[] }>();
    const entries = values.onlineRequestBlockedDates;
    const text = (key: string) => i18n.t(`restaurant.reservationSettings.blockedDates.${key}`);

    function updateEntries(change: (entries: ReservationBlockedDate[]) => ReservationBlockedDate[]) {
        setValues((current) => ({
            ...current, onlineRequestBlockedDates: change(current.onlineRequestBlockedDates),
        }));
    }

    function update(index: number, field: 'fromDate' | 'toDate' | 'reason', value: string | null) {
        updateEntries((entries) => entries.map((entry, current) =>
            current === index ? { ...entry, [field]: value } : entry
        ));
    }

    return (
        <Box mt={3} id="onlineRequestBlockedDates" tabIndex={-1}>
            <Typography variant="caption" display="block" gutterBottom>
                {text('title').toUpperCase()}
            </Typography>
            <Typography variant="body2" color="textSecondary">{text('helper')}</Typography>
            {!entries.length && (
                <Box my={2}><Typography variant="body2">{text('empty')}</Typography></Box>
            )}
            {entries.map((entry, index) => {
                const error = submitCount > 0
                    ? getIn(errors, `onlineRequestBlockedDates.${index}.reason`)
                    : undefined;
                return (
                    <Box key={entry.id} mt={2}>
                        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
                            <TextField
                                id={`blocked-date-${entry.id}-from`}
                                type="date" variant="filled" required
                                label={text('from')} value={entry.fromDate}
                                InputLabelProps={{ shrink: true }}
                                disabled={isSubmitting}
                                onChange={(event) => update(index, 'fromDate', event.target.value)}
                            />
                            <TextField
                                id={`blocked-date-${entry.id}-to`}
                                type="date" variant="filled"
                                label={text('to')} value={entry.toDate ?? ''}
                                InputLabelProps={{ shrink: true }}
                                disabled={isSubmitting}
                                onChange={(event) => update(index, 'toDate', event.target.value || null)}
                            />
                            <TextField
                                id={`blocked-date-${entry.id}-reason`}
                                variant="filled" required label={text('reason')} value={entry.reason}
                                style={{ flex: '1 1 260px' }}
                                inputProps={{ maxLength: 100 }}
                                disabled={isSubmitting}
                                onChange={(event) => update(index, 'reason', event.target.value)}
                            />
                            <Button color="primary" disabled={isSubmitting} onClick={() =>
                                updateEntries((entries) =>
                                    entries.filter((_, current) => current !== index))
                            }>{'\u2013 '}{text('remove')}</Button>
                        </Box>
                        {error && <FormHelperText error role="alert" style={{ whiteSpace: 'pre-line' }}>{error}</FormHelperText>}
                    </Box>
                );
            })}
            <Box mt={1}>
                <Typography variant="caption" color="textSecondary">{text('reasonHelper')}</Typography>
            </Box>
            <Button color="primary" disabled={isSubmitting} onClick={() =>
                updateEntries((entries) => [...entries, {
                    id: new ObjectID().toHexString(), fromDate: '', toDate: null, reason: '',
                }])
            }>+ {text('add')}</Button>
        </Box>
    );
}
